import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let id = localStorage.getItem("nb_session");
  if (!id) {
    id = `nb_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    localStorage.setItem("nb_session", id);
  }
  return id;
};

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const sessionId = useMemo(getSessionId, []);
  const [cart, setCart] = useState({ items: [], subtotal: 0, discount: 0, shipping: 0, total: 0, count: 0, coupon: null, free_shipping: null });
  const [drawerOpen, setDrawerOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/cart/${sessionId}`);
      setCart(data);
    } catch (e) {
      console.error(e);
    }
  }, [sessionId]);

  useEffect(() => { refresh(); }, [refresh]);

  const add = useCallback(async (productId, qty = 1, openDrawer = true, variantId = null) => {
    try {
      const { data } = await axios.post(`${API}/cart/${sessionId}/items`, { product_id: productId, variant_id: variantId, qty });
      setCart(data);
      toast.success("Added to your basket");
      if (openDrawer) setDrawerOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add item. Please try again.");
    }
  }, [sessionId]);

  const update = useCallback(async (productId, qty, variantId = null) => {
    const { data } = await axios.patch(`${API}/cart/${sessionId}/items/${productId}`, { qty }, { params: variantId ? { variant_id: variantId } : {} });
    setCart(data);
  }, [sessionId]);

  const remove = useCallback(async (productId, variantId = null) => {
    const { data } = await axios.delete(`${API}/cart/${sessionId}/items/${productId}`, { params: variantId ? { variant_id: variantId } : {} });
    setCart(data);
  }, [sessionId]);

  const applyCoupon = useCallback(async (code) => {
    const { data } = await axios.post(`${API}/cart/${sessionId}/coupon`, { code });
    setCart(data);
    return data;
  }, [sessionId]);

  const removeCoupon = useCallback(async () => {
    const { data } = await axios.delete(`${API}/cart/${sessionId}/coupon`);
    setCart(data);
  }, [sessionId]);

  const value = useMemo(
    () => ({ cart, add, update, remove, applyCoupon, removeCoupon, refresh, drawerOpen, setDrawerOpen }),
    [cart, add, update, remove, applyCoupon, removeCoupon, refresh, drawerOpen]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
